import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useReducedMotion } from 'framer-motion';
import { SectionContainer } from '../SectionContainer';
import { ScrollReveal } from '../ScrollReveal';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Animated line chart showing exponential vs linear growth.
 * Draws in over ~1.6s when scrolled into view.
 */
function GrowthChart() {
  const prefersReduced = useReducedMotion();
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Generate data points
  const labels = Array.from({ length: 12 }, (_, i) => i);
  const exponentialData = labels.map((t) => Math.pow(1.55, t));
  const linearData = labels.map((t) => t * 1.2 + 0.5);

  useEffect(() => {
    if (prefersReduced) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [prefersReduced, hasAnimated]);

  const data = {
    labels,
    datasets: [
      {
        label: 'yetzira.ai (self-building)',
        data: exponentialData,
        borderColor: '#38BDF8',
        backgroundColor: 'rgba(56,189,248,0.08)',
        fill: true,
        tension: 0.45,
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: 'Traditional (scales with headcount)',
        data: linearData,
        borderColor: 'rgba(154,163,178,0.5)',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0,
        borderWidth: 1.5,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: prefersReduced
      ? false
      : {
          duration: hasAnimated ? 1600 : 0,
          easing: 'easeInOutQuart',
        },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: '#9AA3B2',
          font: { family: "'Inter', sans-serif", size: 11 },
          boxWidth: 14,
          padding: 16,
          usePointStyle: true,
          pointStyle: 'line',
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time',
          color: '#9AA3B2',
          font: { family: "'Inter', sans-serif", size: 11 },
        },
        ticks: { display: false },
        grid: { color: 'rgba(255,255,255,0.04)' },
        border: { color: 'rgba(255,255,255,0.08)' },
      },
      y: {
        title: {
          display: true,
          text: 'Capability',
          color: '#9AA3B2',
          font: { family: "'Inter', sans-serif", size: 11 },
        },
        ticks: { display: false },
        grid: { color: 'rgba(255,255,255,0.04)' },
        border: { color: 'rgba(255,255,255,0.08)' },
      },
    },
  };

  return (
    <Box ref={containerRef} h={{ base: '280px', md: '360px' }} w="100%">
      {(prefersReduced || hasAnimated) && (
        <Line ref={chartRef} data={data} options={options} />
      )}
    </Box>
  );
}

export default function ExponentialSection() {
  return (
    <SectionContainer bg="bg.800" id="exponential" aria-labelledby="exp-heading">
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
        gap={{ base: 10, lg: 16 }}
        alignItems="center"
      >
        {/* Copy */}
        <VStack align="flex-start" spacing={6}>
          <ScrollReveal>
            <Text
              id="exp-heading"
              as="h2"
              fontFamily="'Space Grotesk', sans-serif"
              fontWeight="700"
              fontSize={{ base: 'clamp(1.8rem, 5vw, 2rem)', md: 'clamp(2rem, 3.5vw, 2.75rem)' }}
              letterSpacing="-0.02em"
              color="text.primary"
              lineHeight="1.15"
            >
              An organization that builds itself —{' '}
              <Box as="span" color="brand.400">
                exponentially.
              </Box>
            </Text>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <Text fontSize="1.05rem" lineHeight="1.7" color="text.muted">
              Traditional companies scale by hiring: capability grows in a straight line, capped by
              headcount productivity. Yetzira scales by creating. Its AgentDeveloper writes the code,
              provisions the cloud, and deploys new agents — and entire new organizations — into
              production.
            </Text>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <Text
              fontSize="1.1rem"
              fontWeight="600"
              color="text.primary"
              borderLeft="3px solid"
              borderColor="brand.500"
              pl={4}
              py={1}
            >
              Every agent it builds makes the next one faster to build.
            </Text>
          </ScrollReveal>

          <ScrollReveal delay={0.3}>
            <Text fontSize="1rem" color="text.muted">
              Linear scales with people.{' '}
              <Box as="span" color="brand.400" fontWeight="500">
                Yetzira scales with itself.
              </Box>
            </Text>
          </ScrollReveal>
        </VStack>

        {/* Chart */}
        <ScrollReveal delay={0.2}>
          <Box
            bg="bg.700"
            border="1px solid"
            borderColor="whiteAlpha.100"
            borderRadius="20px"
            p={{ base: '20px', md: '28px' }}
          >
            <Text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="0.75rem"
              color="brand.300"
              textTransform="uppercase"
              letterSpacing="1px"
              mb={4}
            >
              Growth Trajectory
            </Text>
            <GrowthChart />
          </Box>
        </ScrollReveal>
      </Grid>
    </SectionContainer>
  );
}
